import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, useQueries } from '@tanstack/react-query';
import {
  ArrowLeft, ChevronRight, Database, Clock,
  Plus, Pencil, History, Upload, Download, Trash2, LayoutGrid, Table2,
} from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, EmptyState, ErrorState, SectionLabel } from '@/acc-ui/primitives/Layout';
import { Badge } from '@/acc-ui/primitives/Badge';
import { Button } from '@/acc-ui/primitives/Button';
import { Sheet } from '@/acc-ui/primitives/Sheet';
import { Dialog } from '@/acc-ui/primitives/Dialog';
import { Input } from '@/acc-ui/primitives/Input';
import { Textarea } from '@/acc-ui/primitives/Textarea';
import { mastersApi } from '@/api/other-apis';
import type { MasterRecordDetail, ApproverSheetImportResult } from '@/api/other-apis';
import { usePermission } from '@/auth/use-permission';
import {
  MASTERS_EDIT, MASTERS_ROLLBACK, MASTERS_IMPORT, MASTERS_EXPORT,
} from '@loanaudit/shared';
import { MasterRecordEditor } from './masters-record-editor';

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  notes: 'Notes / Description',
  category: 'Category',
  severity: 'Severity',
  maxLoanCap: 'Max Loan Cap',
  maxLoanCapForCategory: 'Max Loan Cap for Category',
  productCode: 'Product Code',
  isSecured: 'Is Secured',
  maxTenureMonths: 'Max Tenure (months)',
  maxFoirPct: 'Max FOIR (%)',
  maxLtvPct: 'Max LTV (%)',
  minAge: 'Min Age (years)',
  maxAge: 'Max Age (years)',
  minCibilScore: 'Min CIBIL Score',
  loanAmountCaps: 'Loan Amount Caps',
  applicableChecklistPoints: 'Applicable Checklist Points',
  minSalary: 'Min Salary',
  geoRestrictions: 'Geo Restrictions',
  // document_checklist master fields
  segment: 'Product / Segment',
  entries: 'Document Entries',
  docType: 'Document Type',
  mandatory: 'Mandatory',
  requiredFields: 'Required Fields',
};
import { cn } from '@/lib/utils';

/**
 * Masters — Sprint 3 full CRUD.
 *
 * Landing: grid of types.
 * Drill-in: record list with New / Import / Export actions.
 * Record drawer: content + history with Rollback and Edit buttons.
 */

export function MastersPage() {
  const { has } = usePermission();
  const canEdit = has(MASTERS_EDIT);
  const canRollback = has(MASTERS_ROLLBACK);
  const canImport = has(MASTERS_IMPORT);
  const canExport = has(MASTERS_EXPORT);

  // Drill-in state lives in the URL so refresh / deep-link / back work.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedType = searchParams.get('type');
  const selectedRecordId = searchParams.get('record');
  const productFilter = searchParams.get('product');
  const patchParams = (patch: Record<string, string | null>) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v == null) next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  const setSelectedType = (v: string | null) => patchParams({ type: v, record: null, product: null });
  const setSelectedRecordId = (v: string | null) => patchParams({ record: v });
  const [editRecordId, setEditRecordId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<{ version: number } | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const typesQuery = useQuery({ queryKey: ['masterTypes'], queryFn: mastersApi.listTypes });
  const typeQuery = useQuery({
    queryKey: ['masterType', selectedType],
    queryFn: () => mastersApi.getType(selectedType!),
    enabled: Boolean(selectedType),
  });
  const recordQuery = useQuery({
    queryKey: ['masterRecord', selectedRecordId],
    queryFn: () => mastersApi.getRecord(selectedRecordId!),
    enabled: Boolean(selectedRecordId),
  });
  // Separate query for the record being edited, so opening the editor never
  // also opens the read-only drawer (they are driven by different state).
  const editRecordQuery = useQuery({
    queryKey: ['masterRecord', editRecordId],
    queryFn: () => mastersApi.getRecord(editRecordId!),
    enabled: Boolean(editRecordId),
  });

  // Product tree: a product's config is spread across a few master types, each
  // keyed by product code (record_key). We group them into a product-first
  // drill-in. Pure view rollup — no schema change.
  //
  // We show ONLY the canonical products via an explicit allow-list. This keeps
  // out (a) retired products still active in a not-yet-reseeded DB, and
  // (b) non-product keys like document_checklist's segment rows ("retail").
  // document_checklist is intentionally NOT a tree type — it's keyed by
  // segment, not product code, so it never maps to a single product yet.
  const CANONICAL_PRODUCTS = ['HOME_LOAN', 'LAP', 'PL', 'MLAP', 'DISHA_HL', 'BL'];
  // Most product-scoped types hold one record per product, so record_key IS the
  // product code. The deviation matrix is the exception: it holds one record per
  // deviation code (PL-BU-10), and names its product in a content field. Such
  // types group by that field instead, and drill in to a filtered list rather
  // than to a single record.
  const PRODUCT_TREE_TYPES: { key: string; facet: string; productField?: string }[] = [
    { key: 'product_policy', facet: 'Policy' },
    { key: 'product_catalog', facet: 'Catalog' },
    { key: 'cibil_thresholds', facet: 'CIBIL threshold' },
    // Record-keyed by product code, so no productField — one ladder per product.
    { key: 'deviation_approvers', facet: 'Approval ladder' },
    { key: 'deviation_level_mapping', facet: 'Deviation matrix', productField: 'products' },
  ];
  /** Types whose product lives in a content field. Key = type, value = field. */
  const PRODUCT_FIELD_BY_TYPE: Record<string, string> = Object.fromEntries(
    PRODUCT_TREE_TYPES.filter((t) => t.productField).map((t) => [t.key, t.productField!]),
  );
  /**
   * Products a record belongs to. A list, not one value: a deviation code can
   * apply to several portfolios at once ("MM,AH"), so it must appear under each
   * product's card. Record-keyed types return their single key.
   */
  const recordProducts = (
    typeKey: string,
    r: { recordKey: string; content?: unknown },
  ): string[] => {
    const field = PRODUCT_FIELD_BY_TYPE[typeKey];
    if (!field) return [r.recordKey];
    const v = (r.content as Record<string, unknown> | undefined)?.[field];
    if (Array.isArray(v)) {
      return v.filter((x): x is string => typeof x === 'string' && x.trim() !== '')
        .map((x) => x.trim());
    }
    // Tolerate the pre-array shape so records written before the change still group.
    return typeof v === 'string' && v.trim() !== '' ? [v.trim()] : [];
  };
  const treeTypeKeys = PRODUCT_TREE_TYPES.filter((t) =>
    (typesQuery.data ?? []).some((x) => x.key === t.key),
  );
  const treeTypeQueries = useQueries({
    queries: treeTypeKeys.map((t) => ({
      queryKey: ['masterType', t.key],
      queryFn: () => mastersApi.getType(t.key),
      enabled: !selectedType && Boolean(typesQuery.data),
    })),
  });
  const productTree = (() => {
    const byProduct = new Map<
      string,
      { facet: string; typeKey: string; recordId: string | null; count?: number }[]
    >();
    treeTypeKeys.forEach((t, i) => {
      const recs = treeTypeQueries[i]?.data?.records ?? [];
      // A content-scoped type contributes ONE facet per product covering all of
      // that product's records, not one facet per record — 48 deviation codes
      // must not render as 48 identical cards.
      if (PRODUCT_FIELD_BY_TYPE[t.key]) {
        const counts = new Map<string, number>();
        for (const r of recs) {
          if (r.isActive === false) continue;
          for (const code of recordProducts(t.key, r)) {
            if (!CANONICAL_PRODUCTS.includes(code)) continue;
            counts.set(code, (counts.get(code) ?? 0) + 1);
          }
        }
        // Include products with no rows yet so the facet still appears and can
        // be opened — an absent facet looks like the feature does not exist.
        for (const code of CANONICAL_PRODUCTS) {
          const count = counts.get(code) ?? 0;
          if (!byProduct.has(code)) byProduct.set(code, []);
          byProduct.get(code)!.push({ facet: t.facet, typeKey: t.key, recordId: null, count });
        }
        return;
      }
      for (const r of recs) {
        const key = r.recordKey;
        if (!CANONICAL_PRODUCTS.includes(key)) continue; // only the 6 products we configure
        if (r.isActive === false) continue;
        if (!byProduct.has(key)) byProduct.set(key, []);
        byProduct.get(key)!.push({ facet: t.facet, typeKey: t.key, recordId: r.id });
      }
    });
    // Order by the canonical list, not alphabetically.
    return [...byProduct.entries()].sort(
      (a, b) => CANONICAL_PRODUCTS.indexOf(a[0]) - CANONICAL_PRODUCTS.indexOf(b[0]),
    );
  })();
  const prettyProduct = (code: string) =>
    code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  // --- Type detail view ---
  // Declared before any early return: a hook after one runs conditionally, so
  // if the types query failed on a refetch after a successful render React
  // would see fewer hooks than last time and throw instead of showing the
  // error state.
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  if (typesQuery.isError) {
    return (
      <AppShell crumb="Admin" title="Masters">
        <ErrorState
          description="Could not load master types."
          action={<Button onClick={() => typesQuery.refetch()}>Retry</Button>}
        />
      </AppShell>
    );
  }

  // --- Product detail view: a product was picked from the grid (no type yet).
  // Shows that product's configs (Policy / Catalog / CIBIL / Documents) as
  // cards, each opening the record editor scoped to this product. ---
  if (!selectedType && productFilter) {
    const facets = productTree.find(([c]) => c === productFilter)?.[1] ?? [];
    return (
      <AppShell crumb="Admin / Masters" title={prettyProduct(productFilter)}>
        <PageHeader
          title={prettyProduct(productFilter)}
          subtitle={`Product configuration · ${productFilter}`}
          actions={
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              onClick={() => patchParams({ product: null, type: null, record: null })}
            >
              Back
            </Button>
          }
        />
        {facets.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Database className="w-5 h-5" />}
              title="No configuration yet"
              description="This product has no master records yet. Re-run the seed, or add records via the master types below."
            />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {facets.map((f) => (
              <button
                key={f.typeKey}
                onClick={() => patchParams({ type: f.typeKey, record: f.recordId })}
                className={cn(
                  'text-left data-surface p-4 hover:border-brand-blue/30 transition-colors',
                  'flex items-center justify-between gap-2',
                )}
              >
                <span className="text-body font-semibold text-text-primary">
                  {f.facet}
                  {f.count !== undefined && (
                    <span className="ml-2 text-caption font-normal text-text-tertiary tabular-nums">
                      {f.count === 0 ? 'not configured yet' : `${f.count} code${f.count === 1 ? '' : 's'}`}
                    </span>
                  )}
                </span>
                <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />
              </button>
            ))}
          </div>
        )}
      </AppShell>
    );
  }

  if (selectedType && typeQuery.data) {
    const t = typeQuery.data;
    // When arriving from the product tree, scope the table to that product so
    // one product's page never shows other products' rows.
    const shownRecords = productFilter
      ? t.records.filter((r) => recordProducts(t.key, r).includes(productFilter))
      : t.records;

    // A content-scoped type with no product picked shows a card per product
    // rather than every product's codes in one flat list. Products appear here
    // as their slices are seeded, so the grid grows on its own.
    if (!productFilter && PRODUCT_FIELD_BY_TYPE[t.key]) {
      const counts = new Map<string, number>();
      let unassigned = 0;
      for (const r of t.records) {
        const codes = recordProducts(t.key, r);
        if (codes.length === 0) unassigned++;
        // Counted once per product: a code shared across portfolios belongs to
        // each of them, so the per-card totals legitimately exceed the row count.
        for (const code of codes) counts.set(code, (counts.get(code) ?? 0) + 1);
      }
      // Every canonical product gets a card, including ones with no codes yet.
      // A product missing from the grid reads as "nothing to configure"; a card
      // showing "not configured yet" states the truth and is somewhere to land.
      for (const code of CANONICAL_PRODUCTS) if (!counts.has(code)) counts.set(code, 0);
      const ordered = [...counts.entries()].sort((a, b) => {
        const ia = CANONICAL_PRODUCTS.indexOf(a[0]);
        const ib = CANONICAL_PRODUCTS.indexOf(b[0]);
        // Canonical products first in their fixed order, anything else after.
        if (ia !== ib) return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        return a[0].localeCompare(b[0]);
      });
      return (
        <AppShell crumb="Admin" title={`Masters / ${t.name}`}>
          <div className="space-y-4">
            <PageHeader
              title={t.name}
              subtitle="Grouped by product. Pick a product to see its deviation codes."
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<ArrowLeft className="w-4 h-4" />}
                  onClick={() => patchParams({ type: null, record: null, product: null })}
                >
                  Back
                </Button>
              }
            />
            {ordered.length === 0 ? (
              <Card>
                <EmptyState
                  icon={<Database className="w-5 h-5" />}
                  title="No products configured yet"
                  description="Run the seed to load a product's deviation matrix."
                />
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {ordered.map(([code, count]) => (
                  <button
                    key={code}
                    onClick={() => patchParams({ product: code, record: null })}
                    className={cn(
                      'text-left data-surface p-4 hover:border-brand-blue/30 transition-colors',
                      'flex flex-col gap-2',
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-body font-semibold text-text-primary truncate">
                          {prettyProduct(code)}
                        </span>
                        <Badge subtle tone="muted">{code}</Badge>
                      </div>
                      <ChevronRight className="w-4 h-4 text-text-muted shrink-0 mt-1" />
                    </div>
                    <div className="flex items-center gap-1 mt-1 text-micro text-text-tertiary tabular-nums">
                      <Database className="w-3 h-3" />
                      {count === 0
                        ? 'Not configured yet'
                        : `${count} deviation code${count === 1 ? '' : 's'}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {unassigned > 0 && (
              // Surfaced rather than hidden: a record with no product would
              // otherwise be unreachable from this screen.
              <Card>
                <p className="text-caption text-text-secondary">
                  {unassigned} record{unassigned === 1 ? '' : 's'} have no{' '}
                  <code className="font-mono">{PRODUCT_FIELD_BY_TYPE[t.key]}</code> set and are not
                  shown in any product card.
                </p>
              </Card>
            )}
          </div>
        </AppShell>
      );
    }
    return (
      <AppShell crumb="Admin" title={`Masters / ${t.name}`}>
        <PageHeader
          title={t.name}
          actions={
            <div className="flex items-center gap-2">
              {/* View toggle */}
              <div className="flex rounded-md border border-glass-hairline overflow-hidden text-micro">
                <button
                  type="button"
                  className={`flex items-center gap-1 px-2.5 py-1.5 transition-colors ${viewMode === 'table' ? 'bg-brand-blue text-white font-semibold' : 'bg-white/60 text-text-secondary hover:bg-slate-50'}`}
                  onClick={() => setViewMode('table')}
                >
                  <Table2 className="w-3.5 h-3.5" /> Table
                </button>
                <button
                  type="button"
                  className={`flex items-center gap-1 px-2.5 py-1.5 transition-colors ${viewMode === 'cards' ? 'bg-brand-blue text-white font-semibold' : 'bg-white/60 text-text-secondary hover:bg-slate-50'}`}
                  onClick={() => setViewMode('cards')}
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Cards
                </button>
              </div>
              {canExport && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download className="w-4 h-4" />}
                  onClick={() => exportType(t)}
                >
                  Export
                </Button>
              )}
              {canImport && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Upload className="w-4 h-4" />}
                  onClick={() => setImportOpen(true)}
                >
                  Import
                </Button>
              )}
              {canEdit && (
                <Button
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    setSelectedRecordId(null);
                    setEditRecordId(null);
                    setCreateOpen(true);
                  }}
                >
                  New record
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeft className="w-4 h-4" />}
                onClick={() => {
                  // Keep the product param so we return to the product detail
                  // view (if we came from there); otherwise this lands on the grid.
                  patchParams({ type: null, record: null });
                }}
              >
                Back
              </Button>
            </div>
          }
        />

        <div className="flex items-center gap-2">
          <Badge subtle tone="muted">
            {t.isSystem ? 'System type' : 'Custom type'}
          </Badge>
          <span className="text-caption text-text-tertiary">
            {t.recordCount} record{t.recordCount === 1 ? '' : 's'}
          </span>
          {productFilter && (
            <button
              type="button"
              onClick={() => patchParams({ product: null })}
              className="inline-flex items-center gap-1 rounded-full border border-brand-blue/30 bg-brand-blue/[0.06] px-2.5 py-0.5 text-micro font-medium text-brand-blue hover:bg-brand-blue/[0.12]"
            >
              Filtered to {productFilter} · Clear
            </button>
          )}
        </div>

        {shownRecords.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Database className="w-5 h-5" />}
              title={
                productFilter
                  ? PRODUCT_FIELD_BY_TYPE[t.key]
                    ? `${productFilter} — not configured yet`
                    : `No ${productFilter} record in this type`
                  : 'No records yet'
              }
              description={
                productFilter
                  ? PRODUCT_FIELD_BY_TYPE[t.key]
                    ? 'No rows have been loaded for this product yet. Add them here, or '
                      + 'run db:import-deviation-matrix to load a product slice in bulk.'
                    : 'This product has no record for this config type. Clear the filter to see all records.'
                  : canEdit
                  ? 'Click "New record" above to add the first one, or Import a batch from JSON.'
                  : 'Records will appear here once an administrator creates them.'
              }
            />
          </Card>
        ) : viewMode === 'table' ? (
          <SpreadsheetTable
            records={shownRecords}
            masterTypeKey={t.key}
            canEdit={canEdit}
            onEdit={(id) => { setSelectedRecordId(null); setEditRecordId(id); }}
            onViewHistory={(id) => setSelectedRecordId(id)}
          />
        ) : (
          <Card padding={false}>
            <ul className="divide-y divide-glass-hairline">
              {shownRecords.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelectedRecordId(r.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-brand-blue/[0.04] text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">{r.recordKey}</span>
                        <Badge tone="muted">v{r.version}</Badge>
                        {!r.isActive && <Badge tone="neutral">inactive</Badge>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-caption text-text-tertiary">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(r.updatedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-text-muted shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Create */}
        {canEdit && createOpen && (
          <MasterRecordEditor
            open={createOpen}
            onOpenChange={setCreateOpen}
            masterTypeKey={t.key}
            schemaJson={t.schemaJson}
            onSaved={() => setCreateOpen(false)}
          />
        )}

        {/* Import — approver ladders come from an HR spreadsheet, everything
            else from a JSON batch. */}
        {canImport && importOpen && t.key === 'deviation_approvers' && (
          <ApproverSheetImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            products={CANONICAL_PRODUCTS}
            initialProduct={productFilter}
            onImported={() => setImportOpen(false)}
          />
        )}
        {canImport && importOpen && t.key !== 'deviation_approvers' && (
          <ImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            masterTypeKey={t.key}
            onImported={() => setImportOpen(false)}
          />
        )}

        {/* Record drawer (read-only view + history + rollback) */}
        {selectedRecordId && recordQuery.data && (
          <RecordDrawer
            record={recordQuery.data}
            canEdit={canEdit}
            canRollback={canRollback}
            rollbackTarget={rollbackTarget}
            setRollbackTarget={setRollbackTarget}
            onRequestEdit={() => {
              const id = selectedRecordId;
              setSelectedRecordId(null);
              setRollbackTarget(null);
              setEditRecordId(id);
            }}
            onClose={() => {
              setSelectedRecordId(null);
              setRollbackTarget(null);
            }}
          />
        )}

        {/* Edit dialog — standalone, never opens the drawer alongside it */}
        {canEdit && editRecordId && editRecordQuery.data && (
          <MasterRecordEditor
            open
            onOpenChange={(v) => !v && setEditRecordId(null)}
            masterTypeKey={t.key}
            schemaJson={t.schemaJson}
            editing={editRecordQuery.data}
            onSaved={() => setEditRecordId(null)}
          />
        )}
      </AppShell>
    );
  }

  // --- Landing: grid of types ---
  return (
    <AppShell crumb="Admin" title="Masters">
      <PageHeader
        title="Masters"
        subtitle="Configurable reference data the audit engine reads from."
      />

      {typesQuery.isLoading && (
        <Card>
          <div className="py-3 text-center text-label text-text-tertiary">Loading…</div>
        </Card>
      )}

      {typesQuery.data && typesQuery.data.length === 0 && (
        <Card>
          <EmptyState
            icon={<Database className="w-5 h-5" />}
            title="No master types yet"
            description="The seeder creates system master types on first deploy. If you see nothing, run npm run db:seed."
          />
        </Card>
      )}

      {/* Products — same card style as the master types below. Clicking a
          product opens its detail view (its policy/catalog/CIBIL configs). */}
      {productTree.length > 0 && (
        <>
          <SectionLabel>Products</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-5">
            {productTree.map(([code, facets]) => (
              <button
                key={code}
                onClick={() => patchParams({ product: code, type: null, record: null })}
                className={cn(
                  'text-left data-surface p-4 hover:border-brand-blue/30 transition-colors',
                  'flex flex-col gap-2',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-body font-semibold text-text-primary truncate">
                        {prettyProduct(code)}
                      </span>
                      <Badge subtle tone="muted">{code}</Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-text-muted shrink-0 mt-1" />
                </div>
                <div className="flex items-center gap-1 mt-1 text-micro text-text-tertiary tabular-nums">
                  <Database className="w-3 h-3" />
                  {facets.length} config{facets.length === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {productTree.length > 0 && <SectionLabel>All master types</SectionLabel>}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(typesQuery.data ?? []).map((t) => (
          <button
            key={t.key}
            onClick={() => setSelectedType(t.key)}
            className={cn(
              'text-left data-surface p-4 hover:border-brand-blue/30 transition-colors',
              'flex flex-col gap-2',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-body font-semibold text-text-primary truncate">
                    {t.name}
                  </span>
                  {t.isSystem && <Badge subtle tone="muted">system</Badge>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-text-muted shrink-0 mt-1" />
            </div>
            {t.description && (
              <p className="text-caption text-text-tertiary line-clamp-1 leading-relaxed">
                {t.description}
              </p>
            )}
            <div className="flex items-center gap-1 mt-1 text-micro text-text-tertiary tabular-nums">
              <Database className="w-3 h-3" />
              {t.recordCount} record{t.recordCount === 1 ? '' : 's'}
            </div>
          </button>
        ))}
      </div>
    </AppShell>
  );
}

// ------------------- Spreadsheet table -------------------

type RecordSummary = {
  id: string;
  recordKey: string;
  content: Record<string, unknown>;
  version: number;
  isActive: boolean;
  updatedAt: string;
};

function SpreadsheetTable({
  records,
  masterTypeKey,
  canEdit,
  onEdit,
  onViewHistory,
}: {
  records: RecordSummary[];
  masterTypeKey: string;
  canEdit: boolean;
  onEdit: (id: string) => void;
  onViewHistory: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [confirmTarget, setConfirmTarget] = useState<RecordSummary | null>(null);

  // Fetch all record contents in parallel
  // Content arrives with the type detail, so no per-record fetch — this was
  // one request per row, 48 of them on a product's deviation matrix alone.
  const columns = useMemo(() => {
    const keySet = new Set<string>();
    for (const r of records) {
      Object.keys(r.content ?? {}).forEach((k) => keySet.add(k));
    }
    return Array.from(keySet);
  }, [records]);

  const getLabel = (key: string) =>
    FIELD_DISPLAY_NAMES[key] ??
    key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (s) => s.toUpperCase()).trim();

  // Deactivate (soft-delete)
  const deactivateMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      mastersApi.updateRecord(id, { content: {} as any, isActive, changeReason: isActive ? 'Reactivated' : 'Deactivated' }),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['masterRecord', id] });
      qc.invalidateQueries({ queryKey: ['masterType', masterTypeKey] });
    },
  });

  function renderCellValue(value: unknown): React.ReactNode {
    if (value === undefined || value === null) return <span className="text-text-muted">—</span>;
    if (typeof value === 'boolean')
      return (
        <span className={`px-2 py-0.5 rounded-full text-micro font-semibold ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
          {value ? 'Yes' : 'No'}
        </span>
      );
    if (typeof value === 'number')
      return <span className="font-semibold tabular-nums">{/cap|amount|salary|loan/i.test('') ? `₹${value.toLocaleString('en-IN')}` : value.toLocaleString('en-IN')}</span>;
    if (typeof value === 'string') return <span>{value}</span>;
    if (Array.isArray(value))
      return (
        <span className="text-micro text-text-muted bg-slate-100 px-1.5 py-0.5 rounded">
          {value.length} item{value.length !== 1 ? 's' : ''}
        </span>
      );
    return (
      <span className="text-micro text-text-muted bg-slate-100 px-1.5 py-0.5 rounded">
        {Object.keys(value as object).length} fields
      </span>
    );
  }

  const inactiveTarget = confirmTarget ? !confirmTarget.isActive : false;

  return (
    <>
    <Card padding={false}>
      <div className="overflow-x-auto">
        <table className="w-full text-caption border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-glass-hairline">
              <th className="px-4 py-2.5 text-left font-semibold text-text-secondary whitespace-nowrap">Key</th>
              {columns.map((col) => (
                <th key={col} className="px-3 py-2.5 text-left font-semibold text-text-secondary whitespace-nowrap">
                  {getLabel(col)}
                </th>
              ))}
              <th className="px-3 py-2.5 text-left font-semibold text-text-secondary whitespace-nowrap">Ver</th>
              <th className="px-3 py-2.5 text-left font-semibold text-text-secondary whitespace-nowrap">Updated</th>
              <th className="px-3 py-2.5 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-glass-hairline">
            {records.map((r) => {
              const content = (r.content ?? {}) as Record<string, unknown>;
              const inactive = !r.isActive;
              return (
                <tr
                  key={r.id}
                  className={`hover:bg-slate-50/60 transition-colors ${inactive ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-2.5 font-medium text-text-primary whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {r.recordKey}
                      {inactive && <Badge tone="neutral">inactive</Badge>}
                    </div>
                  </td>
                  {columns.map((col) => (
                    <td key={col} className="px-3 py-2.5">
                      {renderCellValue(content[col])}
                    </td>
                  ))}
                  <td className="px-3 py-2.5 text-text-muted">v{r.version}</td>
                  <td className="px-3 py-2.5 text-text-muted whitespace-nowrap">
                    {new Date(r.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <button
                          onClick={() => onEdit(r.id)}
                          className="p-1.5 rounded hover:bg-brand-blue/10 text-brand-blue transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => onViewHistory(r.id)}
                        className="p-1.5 rounded hover:bg-slate-100 text-text-muted transition-colors"
                        title="View history"
                      >
                        <History className="w-3.5 h-3.5" />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => setConfirmTarget(r)}
                          className={`p-1.5 rounded transition-colors ${inactive ? 'hover:bg-emerald-50 text-emerald-600' : 'hover:bg-red-50 text-red-500'}`}
                          title={inactive ? 'Reactivate' : 'Deactivate'}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 border-t border-glass-hairline text-micro text-text-muted">
        {records.length} record{records.length !== 1 ? 's' : ''}
      </div>
    </Card>

    <Dialog
      open={confirmTarget !== null}
      onOpenChange={(v) => !v && setConfirmTarget(null)}
      title={inactiveTarget ? 'Reactivate record?' : 'Deactivate record?'}
      description={
        confirmTarget
          ? inactiveTarget
            ? `"${confirmTarget.recordKey}" will be reactivated and read by the audit engine again.`
            : `"${confirmTarget.recordKey}" will be deactivated. The audit engine will stop reading it.`
          : undefined
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => setConfirmTarget(null)}>
            Cancel
          </Button>
          <Button
            variant={inactiveTarget ? 'primary' : 'danger'}
            loading={deactivateMut.isPending}
            onClick={() => {
              if (confirmTarget)
                deactivateMut.mutate(
                  { id: confirmTarget.id, isActive: !confirmTarget.isActive },
                  { onSuccess: () => setConfirmTarget(null) },
                );
            }}
          >
            {inactiveTarget ? 'Reactivate' : 'Deactivate'}
          </Button>
        </>
      }
    >
      <p className="text-caption text-text-tertiary">A new version is recorded in history.</p>
    </Dialog>
    </>
  );
}

function exportType(t: { key: string; records: Array<{ recordKey: string; id: string }> }) {
  // Lightweight export: produce a JSON file with recordKey -> placeholder
  // (full content requires getRecord per id; keep export UI simple by
  // shipping the list of keys + version). Extended export is a menu we
  // can wire later to a dedicated endpoint.
  const payload = {
    masterTypeKey: t.key,
    records: t.records.map((r) => ({ id: r.id, recordKey: r.recordKey })),
    exportedAt: new Date().toISOString(),
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `masters-${t.key}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

// ------------------- Record drawer -------------------

function RecordDrawer({
  record,
  canEdit,
  canRollback,
  rollbackTarget,
  setRollbackTarget,
  onRequestEdit,
  onClose,
}: {
  record: MasterRecordDetail;
  canEdit: boolean;
  canRollback: boolean;
  rollbackTarget: { version: number } | null;
  setRollbackTarget: (v: { version: number } | null) => void;
  onRequestEdit: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <Sheet
        open
        onOpenChange={(v) => !v && onClose()}
        side="right"
        title={record.recordKey}
        widthPx={560}
      >
        <div className="flex flex-col gap-3">
          <div className="p-3 bg-white/60 rounded-glass-sm border border-glass-hairline">
            <div className="flex items-center justify-between">
              <SectionLabel>{record.masterTypeName}</SectionLabel>
              <Badge tone="muted">v{record.version}</Badge>
            </div>
            <div className="mt-2 flex items-center gap-3 text-micro text-text-tertiary">
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {new Date(record.updatedAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>

          {canEdit && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                leftIcon={<Pencil className="w-3.5 h-3.5" />}
                onClick={onRequestEdit}
              >
                Edit
              </Button>
            </div>
          )}

          <div>
            <SectionLabel>Content</SectionLabel>
            <div className="mt-2 rounded-lg border border-glass-hairline bg-white/80 divide-y divide-glass-hairline">
              {Object.entries(record.content as Record<string, unknown>).map(([key, value]) => (
                <div key={key} className="flex items-start gap-3 px-4 py-2.5">
                  <span className="w-[40%] text-caption font-medium text-text-tertiary shrink-0">
                    {FIELD_DISPLAY_NAMES[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, s => s.toUpperCase()).trim()}
                  </span>
                  <span className="flex-1 text-caption text-text-primary">
                    {value === null ? (
                      <span className="text-text-tertiary italic">—</span>
                    ) : typeof value === 'boolean' ? (
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-micro font-semibold ${value ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {value ? 'Yes' : 'No'}
                      </span>
                    ) : typeof value === 'number' ? (
                      <span className="font-semibold tabular-nums">
                        {/cap|amount|salary|loan/i.test(key)
                          ? `₹${value.toLocaleString('en-IN')}`
                          : value.toLocaleString('en-IN')}
                      </span>
                    ) : typeof value === 'string' ? (
                      value
                    ) : Array.isArray(value) ? (
                      <span className="text-text-secondary">{JSON.stringify(value)}</span>
                    ) : (
                      <pre className="text-micro bg-slate-50 rounded px-2 py-1 overflow-x-auto m-0">
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <SectionLabel>
              History ({record.history.length})
            </SectionLabel>
            {record.history.length === 0 ? (
              <p className="mt-2 text-caption text-text-tertiary italic">
                No history yet. Every edit produces a new version here.
              </p>
            ) : (
              <ul className="mt-2 divide-y divide-glass-hairline border border-glass-hairline rounded-glass-sm">
                {record.history.map((h) => (
                  <li
                    key={h.version}
                    className="px-3 py-2 flex items-center justify-between text-caption"
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <Badge tone="muted">v{h.version}</Badge>
                      <span className="text-text-tertiary tabular-nums">
                        {new Date(h.createdAt).toLocaleString()}
                      </span>
                      <span className="text-text-tertiary truncate max-w-[16rem]">
                        {h.changeReason ?? '—'}
                      </span>
                    </span>
                    {canRollback && h.version < record.version && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<History className="w-3.5 h-3.5" />}
                        onClick={() => setRollbackTarget({ version: h.version })}
                      >
                        Rollback
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Sheet>

      {/* Rollback confirmation */}
      {canRollback && rollbackTarget && (
        <RollbackDialog
          record={record}
          target={rollbackTarget}
          onOpenChange={(v) => !v && setRollbackTarget(null)}
        />
      )}
    </>
  );
}

function RollbackDialog({
  record,
  target,
  onOpenChange,
}: {
  record: MasterRecordDetail;
  target: { version: number };
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      mastersApi.rollbackRecord(record.id, {
        targetVersion: target.version,
        changeReason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['masterRecord', record.id] });
      qc.invalidateQueries({ queryKey: ['masterType', record.masterTypeKey] });
      onOpenChange(false);
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Rollback failed'),
  });

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={`Roll back ${record.recordKey} to v${target.version}?`}
      description={`This creates a new version (v${
        record.version + 1
      }) with the content from v${target.version}. The current version stays in history.`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setErr(null);
              if (!reason.trim()) {
                setErr('Please provide a reason for the rollback');
                return;
              }
              mut.mutate();
            }}
            loading={mut.isPending}
          >
            Confirm rollback
          </Button>
        </>
      }
    >
      <label className="flex flex-col gap-1">
        <span className="text-caption font-semibold text-text-secondary">
          Reason <span className="text-red-700">*</span>
        </span>
        <Input
          placeholder="Why are we rolling back?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {err && (
        <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {err}
        </div>
      )}
    </Dialog>
  );
}

// ------------------- Approver ladder sheet import -------------------

/**
 * Upload the approval ladder HR sends, for one product.
 *
 * The generic ImportDialog below takes hand-written JSON, which is the wrong
 * ask for an auditor holding a spreadsheet. This takes the file itself —
 * .xlsx, .csv or tab-separated — and the server reads the columns by header
 * keyword, so HR's wording does not have to match ours.
 */
function ApproverSheetImportDialog({
  open,
  onOpenChange,
  products,
  initialProduct,
  onImported,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  products: string[];
  initialProduct: string | null;
  onImported: () => void;
}) {
  const qc = useQueryClient();
  const [productCode, setProductCode] = useState(initialProduct ?? products[0] ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<ApproverSheetImportResult | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a spreadsheet first');
      // Read as base64 — the endpoint takes the file in the JSON body, since a
      // ladder is a handful of rows and multipart would be more plumbing.
      const contentBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
        reader.onload = () => {
          const out = String(reader.result ?? '');
          resolve(out.slice(out.indexOf(',') + 1)); // strip the data: prefix
        };
        reader.readAsDataURL(file);
      });
      return mastersApi.importApproverSheet({
        productCode,
        fileName: file.name,
        contentBase64,
      });
    },
    onSuccess: (r) => {
      setResult(r);
      setErr(null);
      qc.invalidateQueries({ queryKey: ['masterType', 'deviation_approvers'] });
      qc.invalidateQueries({ queryKey: ['masterRecord'] });
      qc.invalidateQueries({ queryKey: ['masterTypes'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Import failed'),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setFile(null);
          setResult(null);
          setErr(null);
          onImported();
        }
        onOpenChange(v);
      }}
      title="Import approval ladder from a spreadsheet"
      description="Upload the sheet HR sends for one product. Levels in the sheet are updated; levels it does not mention keep their current values."
      size="lg"
      footer={
        result ? (
          <Button variant="primary" size="sm" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={mut.isPending}
              disabled={!file || !productCode}
              onClick={() => mut.mutate()}
            >
              Import
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-label">
          <p className="text-text-primary">
            Imported <strong>{result.productCode}</strong> from {result.fileName}.
          </p>
          <ul className="list-disc ml-5 text-text-secondary space-y-1">
            <li>
              Levels updated: {result.levelsImported.join(', ') || 'none'} (
              {result.approverIdCount} approver ID
              {result.approverIdCount === 1 ? '' : 's'})
            </li>
            {result.levelsUntouched.length > 0 && (
              <li>Levels left unchanged: {result.levelsUntouched.join(', ')}</li>
            )}
            <li>
              Columns read — level: “{result.columnsUsed.level}”
              {result.columnsUsed.role ? `, role: “${result.columnsUsed.role}”` : ''}
              {result.columnsUsed.approvers.length
                ? `, approvers: ${result.columnsUsed.approvers.map((c) => `“${c}”`).join(', ')}`
                : ''}
            </li>
          </ul>
          {result.skipped.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="font-semibold text-amber-900">
                {result.skipped.length} row{result.skipped.length === 1 ? '' : 's'} skipped
              </p>
              <ul className="mt-1 ml-4 list-disc text-caption text-amber-900">
                {result.skipped.slice(0, 8).map((s) => (
                  <li key={s.row}>
                    Row {s.row}: {s.why}
                  </li>
                ))}
                {result.skipped.length > 8 && <li>… {result.skipped.length - 8} more</li>}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <label className="block">
            <span className="text-micro font-semibold uppercase tracking-wide text-on-surface-variant">
              Product
            </span>
            <select
              className="mt-1 w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-label outline-none focus:border-primary/50"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            >
              {products.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-micro font-semibold uppercase tracking-wide text-on-surface-variant">
              Spreadsheet
            </span>
            <input
              type="file"
              accept=".xlsx,.csv,.tsv,.txt"
              className="mt-1 w-full rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-2 text-label file:mr-3 file:rounded file:border-0 file:bg-brand-blue file:px-3 file:py-1 file:text-white"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setErr(null);
              }}
            />
            <span className="mt-1 block text-caption text-text-tertiary">
              .xlsx, .csv or tab-separated. Legacy .xls is not readable — re-save it as .xlsx.
            </span>
          </label>

          <div className="rounded-lg border border-outline-variant bg-surface-container-low p-3">
            <p className="text-caption font-semibold text-text-secondary">Expected columns</p>
            <p className="mt-1 text-caption text-text-tertiary">
              A header row containing <strong>Level</strong>, plus <strong>Role</strong> and/or
              an approver ID column. Headers are matched loosely, so “Authority Level”,
              “Designation” and “Employee ID” all work, and several ID columns (“Approver 1”,
              “Approver 2”) are all read. IDs may also be comma-separated in one cell.
            </p>
            <pre className="mt-2 overflow-x-auto rounded bg-surface-container-lowest p-2 text-micro text-text-secondary">
{`Level  Role                  Approver IDs
1      Credit Manager        AFL812, AFL815
2      Senior Credit Manager AFL828
7      ZCM                   AFL983`}
            </pre>
          </div>

          {err && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-label text-red-800">
              {err}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ------------------- Import dialog -------------------

function ImportDialog({
  open,
  onOpenChange,
  masterTypeKey,
  onImported,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  masterTypeKey: string;
  onImported: () => void;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [reason, setReason] = useState('Bulk import');
  const [err, setErr] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ total: number; succeeded: number; failed: number } | null>(
    null,
  );

  const example = useMemo(
    () =>
      JSON.stringify(
        {
          records: [
            { recordKey: 'example_key_1', content: { note: 'example A' } },
            { recordKey: 'example_key_2', content: { note: 'example B' } },
          ],
        },
        null,
        2,
      ),
    [],
  );

  const mut = useMutation({
    mutationFn: async () => {
      let parsed: { records: Array<{ recordKey: string; content: Record<string, unknown> }> };
      try {
        parsed = JSON.parse(text);
      } catch (e) {
        throw new Error(`Not valid JSON: ${(e as Error).message}`);
      }
      if (!Array.isArray(parsed.records) || parsed.records.length === 0) {
        throw new Error('Expected an object with a non-empty "records" array');
      }
      return mastersApi.importRecords({
        masterTypeKey,
        records: parsed.records,
        changeReason: reason.trim() || 'Bulk import',
      });
    },
    onSuccess: (result) => {
      setSummary({
        total: result.total,
        succeeded: result.succeeded,
        failed: result.total - result.succeeded,
      });
      qc.invalidateQueries({ queryKey: ['masterType', masterTypeKey] });
      qc.invalidateQueries({ queryKey: ['masterTypes'] });
      // Import updates existing records too — refresh their cached content so
      // the spreadsheet table doesn't show stale values until a reload.
      qc.invalidateQueries({ queryKey: ['masterRecord'] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Import failed'),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          setText('');
          setSummary(null);
          setErr(null);
          onImported();
        }
        onOpenChange(v);
      }}
      title={`Import records into ${masterTypeKey}`}
      description="Paste a JSON object with a records array. Each record needs a recordKey and content. Existing keys are updated (new version); new keys are created."
      size="lg"
      footer={
        summary ? (
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setErr(null);
                mut.mutate();
              }}
              loading={mut.isPending}
            >
              Import
            </Button>
          </>
        )
      }
    >
      {summary ? (
        <div className="flex flex-col gap-2">
          <div className="rounded-glass-sm border border-glass-hairline bg-white/60 p-3">
            <div className="text-label text-text-primary">
              Imported <strong>{summary.succeeded}</strong> of {summary.total} records.
            </div>
            {summary.failed > 0 && (
              <div className="text-caption text-red-700 mt-1">
                {summary.failed} records failed. Check the audit log for details.
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-caption font-semibold text-text-secondary">JSON payload</span>
            <Textarea
              rows={12}
              spellCheck={false}
              placeholder={example}
              className="font-mono text-caption leading-relaxed"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-caption font-semibold text-text-secondary">Change reason</span>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Q2 policy refresh"
            />
          </label>
          {err && (
            <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {err}
            </div>
          )}
        </>
      )}
    </Dialog>
  );
}

